import { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { KeyRound, Loader2, HelpCircle } from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { savePortalAuth, getPortalToken } from "@/lib/portalAuth";

export default function PortalLoginPage() {
  const nav = useNavigate();
  const [enrollmentId, setEnrollmentId] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSending, setForgotSending] = useState(false);

  if (getPortalToken()) return <Navigate to="/portal" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/portal/login", {
        enrollment_id: enrollmentId.trim().toUpperCase(),
        password,
      });
      savePortalAuth(data);
      toast.success(`Welcome, ${data.parent_name}.`);
      nav("/portal");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setForgotSending(true);
    try {
      await api.post("/portal/password/forgot", { email: forgotEmail.trim() });
      toast.success("If an account exists for that email, a reset link is on its way.");
      setShowForgot(false);
      setForgotEmail("");
    } catch {
      toast.error("Could not send reset email. Please try again.");
    } finally {
      setForgotSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-grey-light flex flex-col">
      <SiteHeader />
      <div className="flex-1 max-w-md w-full mx-auto px-5 py-16">
        <div className="border border-grey bg-white p-10">
          <div className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-gold text-ink mb-6">
            <KeyRound size={20} />
          </div>
          <div className="text-xs tracking-[0.22em] uppercase text-purple-light font-bold mb-3">
            Parent portal
          </div>
          <h1 className="h-serif text-3xl text-purple-dark mb-2 tracking-tight">
            Welcome back.
          </h1>
          <p className="text-sm text-ink/70 mb-6 leading-relaxed">
            Sign in with your enrollment ID and the password you set when you
            enrolled.
          </p>

          {!showForgot ? (
            <form onSubmit={submit} className="space-y-5" data-testid="portal-login-form">
              <div>
                <Label className="text-xs tracking-[0.15em] uppercase text-purple-dark font-bold mb-2 block">
                  Enrollment ID
                </Label>
                <Input
                  value={enrollmentId}
                  onChange={(e) => setEnrollmentId(e.target.value)}
                  required
                  placeholder="e.g. RMJO8E69"
                  className="border-grey focus:border-purple focus:ring-purple font-mono tracking-wider uppercase"
                  data-testid="portal-enrollment-id"
                  autoFocus
                />
              </div>
              <div>
                <Label className="text-xs tracking-[0.15em] uppercase text-purple-dark font-bold mb-2 block">
                  Password
                </Label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="border-grey focus:border-purple focus:ring-purple"
                  data-testid="portal-password"
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-purple text-white hover:bg-purple-dark font-bold py-3 rounded-sm text-sm transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2"
                data-testid="portal-login-btn"
              >
                {submitting && <Loader2 size={16} className="animate-spin" />}
                {submitting ? "Signing in..." : "Sign in"}
              </button>
              <div className="flex items-center justify-between pt-2 text-xs">
                <button
                  type="button"
                  onClick={() => setShowForgot(true)}
                  className="text-purple hover:text-purple-dark inline-flex items-center gap-1"
                  data-testid="forgot-password-link"
                >
                  <HelpCircle size={12} /> Forgot password?
                </button>
                <Link to="/enroll" className="text-ink/70 hover:text-purple">
                  Need to enroll →
                </Link>
              </div>
            </form>
          ) : (
            <form onSubmit={submitForgot} className="space-y-5" data-testid="forgot-password-form">
              <div>
                <Label className="text-xs tracking-[0.15em] uppercase text-purple-dark font-bold mb-2 block">
                  Email on file
                </Label>
                <Input
                  type="email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  required
                  className="border-grey focus:border-purple focus:ring-purple"
                  data-testid="forgot-email-input"
                  autoFocus
                />
                <p className="text-xs text-ink/60 mt-2 leading-relaxed">
                  We&apos;ll send a password reset link to this address. The link
                  expires in one hour.
                </p>
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowForgot(false)}
                  className="flex-1 border border-grey text-ink hover:bg-grey-light font-bold py-3 rounded-sm text-sm"
                  data-testid="forgot-cancel-btn"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={forgotSending}
                  className="flex-1 bg-purple text-white hover:bg-purple-dark font-bold py-3 rounded-sm text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60"
                  data-testid="forgot-send-btn"
                >
                  {forgotSending && <Loader2 size={14} className="animate-spin" />}
                  Send reset link
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
      <SiteFooter />
    </div>
  );
}
